"use client"

import type { LinkProps } from "@/modules/routing/components/Link"
import type { TrackProperties } from "@/modules/tracking/generated-types/track"
import type { TrackEventProperties } from "@/modules/tracking/types"
import type { AnchorHTMLAttributes, FunctionComponent, MouseEventHandler, PropsWithChildren } from "react"

import { Link } from "@/modules/routing/components/Link"
import { getClientPageType } from "@/modules/routing/routingManager/clientRoutingManager"
import { useTracking } from "@/modules/tracking/useTracking"

type BaseProps =
  | {
      trackingProps: {
        /**
         * If no `eventName` is defined, the current feature page will be guessed and the properties
         * will be passed to the next navigation event (aka page view).
         */
        eventName?: never
        eventProperties: TrackProperties[keyof TrackProperties]
      }
    }
  | { trackingProps: TrackEventProperties }

type CustomLinkProps = BaseProps & LinkProps & { isNativeLink?: boolean }
type NativeLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & BaseProps & PropsWithChildren<{ isNativeLink: true }>

type TrackLinkProps = CustomLinkProps | NativeLinkProps

export const TrackLink: FunctionComponent<TrackLinkProps> = ({
  children,
  isNativeLink = false,
  trackingProps: { eventName, eventProperties },
  ...props
}) => {
  const tracking = useTracking()

  const handleClick: MouseEventHandler<HTMLAnchorElement> = (event): void => {
    if (!eventName && eventProperties) {
      const linkUrl = new URL(event.currentTarget.href)

      tracking?.setRefererParams(
        {
          ...eventProperties,
          from_feature: getClientPageType() || "unknown",
        },
        { keyName: linkUrl.pathname }
      )
    }

    if (eventName) {
      tracking?.track(eventName, eventProperties)
    }

    if (props.onClick) props.onClick(event)
  }

  if (isNativeLink) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <a {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)} onClick={handleClick}>
        {children}
      </a>
    )
  }
  return (
    <Link {...(props as LinkProps)} onClick={handleClick}>
      {children}
    </Link>
  )
}
